/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// src/components/Fraseologia/LogComunicazioniModal.tsx
import React, { useState } from 'react';
import { useAppStore } from '../../store/useAppStore';
import './LogComunicazioniModal.css';

interface LogComunicazioniModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LogComunicazioniModal: React.FC<LogComunicazioniModalProps> = ({ isOpen, onClose }) => {
  const [filtroTipo, setFiltroTipo] = useState<'all' | 'operativa' | 'sicurezza' | 'avvicinamento'>('all');
  const [ricerca, setRicerca] = useState('');
  
  const logComunicazioni = useAppStore(state => state.logComunicazioni);
  const clearLogComunicazioni = useAppStore(state => state.clearLogComunicazioni);

  if (!isOpen) return null;

  // FILTRA LOG
  const logFiltrato = logComunicazioni.filter(log => {
    const matchTipo = filtroTipo === 'all' || log.tipo === filtroTipo;
    const matchRicerca = ricerca === '' || 
      log.messaggio.toLowerCase().includes(ricerca.toLowerCase()) ||
      log.mittente.toLowerCase().includes(ricerca.toLowerCase()) ||
      log.destinatario.toLowerCase().includes(ricerca.toLowerCase());
    
    return matchTipo && matchRicerca;
  });

  // STATISTICHE
  const stats = {
    totali: logComunicazioni.length,
    operative: logComunicazioni.filter(l => l.tipo === 'operativa').length,
    sicurezza: logComunicazioni.filter(l => l.tipo === 'sicurezza').length,
    avvicinamento: logComunicazioni.filter(l => l.tipo === 'avvicinamento').length,
    mayday: logComunicazioni.filter(l => l.priorita === 'mayday').length
  };

  // EXPORT PDF tramite finestra di stampa
  const handleExportPDF = () => {
    const dataExport = new Date().toLocaleString('it-IT');
    const righe = logFiltrato.map(log => `
      <tr style="border-bottom:1px solid #ddd;">
        <td style="padding:6px;white-space:nowrap;">${log.data} ${log.timestamp}</td>
        <td style="padding:6px;">${log.tipo.toUpperCase()}</td>
        <td style="padding:6px;">${log.mittente}</td>
        <td style="padding:6px;">${log.destinatario}</td>
        <td style="padding:6px;">${log.messaggio}</td>
        <td style="padding:6px;color:${log.priorita === 'mayday' ? 'red' : log.priorita === 'urgente' ? 'orange' : 'inherit'};">${(log.priorita || 'normale').toUpperCase()}</td>
      </tr>
    `).join('');

    const html = `
      <html>
      <head>
        <meta charset="utf-8"/>
        <title>Log Comunicazioni DOS518</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
          h1 { font-size: 16px; margin-bottom: 4px; }
          p { margin: 2px 0; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th { background: #ff6b35; color: white; padding: 8px 6px; text-align: left; }
          tr:nth-child(even) { background: #f9f9f9; }
          @media print { body { margin: 10px; } }
        </style>
      </head>
      <body>
        <h1>📋 Log Comunicazioni — DOS518</h1>
        <p>Esportato il: ${dataExport}</p>
        <p>Totale comunicazioni: ${logFiltrato.length}</p>
        <table>
          <thead>
            <tr>
              <th>Data/Ora</th><th>Tipo</th><th>Da</th><th>A</th><th>Messaggio</th><th>Priorità</th>
            </tr>
          </thead>
          <tbody>${righe}</tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  // EXPORT EXCEL (CSV)
  const handleExportExcel = () => {
    const csv = [
      'Data,Ora,Tipo,Da,A,Messaggio,Priorità',
      ...logComunicazioni.map(log => 
        `${log.data},${log.timestamp},${log.tipo},${log.mittente},${log.destinatario},"${log.messaggio}",${log.priorita || 'normale'}`
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `log_comunicazioni_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // CANCELLA LOG
  const handleClearLog = () => {
    if (confirm('⚠️ Sei sicuro di voler cancellare TUTTO il log delle comunicazioni?\n\nQuesta operazione è irreversibile!')) {
      clearLogComunicazioni();
    }
  };

  // ICONE PER TIPO
  const getTipoIcon = (tipo: string) => {
    switch(tipo) {
      case 'operativa': return '📡';
      case 'sicurezza': return '⚠️';
      case 'avvicinamento': return '🚁';
      default: return '📋';
    }
  };

  // COLORE PER PRIORITÀ
  const getPrioritaColor = (priorita?: string) => {
    switch(priorita) {
      case 'mayday': return '#e74c3c';
      case 'urgente': return '#f39c12';
      default: return 'transparent';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content log-modal" onClick={e => e.stopPropagation()}>
        {/* HEADER */}
        <div className="modal-header">
          <h3>📋 LOG COMUNICAZIONI</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {/* STATISTICHE */}
        <div className="log-stats">
          <div className="stat-item">
            <div className="stat-value">{stats.totali}</div>
            <div className="stat-label">Totali</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.operative}</div>
            <div className="stat-label">📡 Operative</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.sicurezza}</div>
            <div className="stat-label">⚠️ Sicurezza</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.avvicinamento}</div>
            <div className="stat-label">🚁 Avvicin.</div>
          </div>
          {stats.mayday > 0 && (
            <div className="stat-item stat-mayday">
              <div className="stat-value">{stats.mayday}</div>
              <div className="stat-label">🚨 MAYDAY</div>
            </div>
          )}
        </div>

        {/* TOOLBAR */}
        <div className="log-toolbar">
          {/* FILTRI TIPO */}
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filtroTipo === 'all' ? 'active' : ''}`}
              onClick={() => setFiltroTipo('all')}
            >
              Tutte
            </button>
            <button 
              className={`filter-btn ${filtroTipo === 'operativa' ? 'active' : ''}`}
              onClick={() => setFiltroTipo('operativa')}
            >
              📡 Operative
            </button>
            <button 
              className={`filter-btn ${filtroTipo === 'sicurezza' ? 'active' : ''}`}
              onClick={() => setFiltroTipo('sicurezza')}
            >
              ⚠️ Sicurezza
            </button>
            <button 
              className={`filter-btn ${filtroTipo === 'avvicinamento' ? 'active' : ''}`}
              onClick={() => setFiltroTipo('avvicinamento')}
            >
              🚁 Avvicin.
            </button>
          </div>

          {/* RICERCA */}
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Cerca nel log..."
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* AZIONI EXPORT */}
        <div className="export-actions">
          <button className="btn-export" onClick={handleExportExcel}>
            📊 Export Excel
          </button>
          <button className="btn-export" onClick={handleExportPDF}>
            📄 Export PDF
          </button>
          <button className="btn-danger-export" onClick={handleClearLog}>
            🗑️ Cancella Log
          </button>
        </div>

        {/* LISTA LOG */}
        <div className="log-list">
          {logFiltrato.length === 0 ? (
            <div className="empty-log">
              {logComunicazioni.length === 0 ? (
                <>
                  <div className="empty-icon">📋</div>
                  <p>Nessuna comunicazione registrata</p>
                  <small>Le comunicazioni inviate verranno salvate automaticamente qui</small>
                </>
              ) : (
                <>
                  <div className="empty-icon">🔍</div>
                  <p>Nessun risultato per "{ricerca}"</p>
                </>
              )}
            </div>
          ) : (
            logFiltrato.map((log) => (
              <div 
                key={log.id} 
                className="log-entry"
                style={{
                  borderLeft: `4px solid ${getPrioritaColor(log.priorita)}`
                }}
              >
                <div className="log-header">
                  <div className="log-time">
                    <span className="log-date">{log.data}</span>
                    <span className="log-timestamp">🕐 {log.timestamp}</span>
                  </div>
                  <div className="log-tipo">
                    {getTipoIcon(log.tipo)} {log.tipo.toUpperCase()}
                  </div>
                </div>

                <div className="log-route">
                  <span className="log-from">{log.mittente}</span>
                  <span className="log-arrow">→</span>
                  <span className="log-to">{log.destinatario}</span>
                </div>

                <div className="log-message">
                  "{log.messaggio}"
                </div>

                {log.priorita && log.priorita !== 'normale' && (
                  <div className={`log-priority priority-${log.priorita}`}>
                    {log.priorita === 'mayday' ? '🚨 MAYDAY' : '⚡ URGENTE'}
                  </div>
                )}

                <div className="log-status">
                  ✅ {log.status || 'Inviata'}
                </div>
              </div>
            ))
          )}
        </div>

        {/* INFO RISULTATI */}
        {logFiltrato.length > 0 && (
          <div className="log-footer">
            Visualizzate {logFiltrato.length} di {logComunicazioni.length} comunicazioni
          </div>
        )}
      </div>
    </div>
  );
};
